export type Language = 'en' | 'ar';

export interface Translations {
  // Common & Navigation
  home: string;
  shop: string;
  phoneCases: string;
  canvasWallArt: string;
  photoFrames: string;
  woodCrafts: string;
  keychains: string;
  drinkingMugs: string;
  apparel: string;
  stickers: string;
  hatsCaps: string;
  customPrint: string;
  blog: string;
  offers: string;
  aboutUs: string;
  faq: string;
  adminPortal: string;
  searchPlaceholder: string;
  searchProducts: string;
  allCategories: string;
  allTags: string;
  resetFilters: string;
  currency: string;
  usd: string;
  switchLanguage: string;
  arabicLebanese: string;
  english: string;

  // Announcement Bar
  announcementLocation: string;
  announcementDefault: string;

  // Hero Banner
  heroEyebrow: string;
  heroHeadline: string;
  heroTagline: string;
  heroWhatsAppBtn: string;
  heroCustomPrintBtn: string;
  heroExploreCatalogBtn: string;
  heroPreviewNote: string;
  heroFastDelivery: string;
  heroFastDeliverySub: string;
  heroDirectWhatsApp: string;
  heroDirectWhatsAppSub: string;
  heroQualityGuarantee: string;
  heroQualityGuaranteeSub: string;

  // Frontpage & Catalog
  customPrintOnDemand: string;
  personalizeByProduct: string;
  viewAllProducts: string;
  allProducts: string;
  showingResults: string;
  noProductsFound: string;
  clearFilters: string;
  orderViaWhatsApp: string;
  quickView: string;
  viewDetails: string;
  startingFrom: string;
  inStock: string;
  outOfStock: string;
  newDrop: string;
  tapToZoom: string;
  productTags: string;
  activeTag: string;
  clearTagFilter: string;
  featuredProducts: string;
  sortByLabel: string;
  sortOptionBlend: string;
  sortOptionNewest: string;
  sortOptionOldest: string;
  loadMoreProducts: string;
  showingItems: string;
  of: string;
  items: string;
  popularCustomProduct: string;
  customPhoneCases: string;
  phoneCasesDesc: string;
  viewAllCases: string;
  cherishedMemories: string;
  customWallCanvas: string;
  wallCanvasDesc: string;
  viewAllCanvas: string;
  newProductDrop: string;
  customPhotoFrames: string;
  photoFramesDesc: string;
  viewAllFrames: string;

  // Product Detail Modal
  customizeAndOrder: string;
  specsAndOptions: string;
  chooseOption: string;
  yourName: string;
  phoneNumber: string;
  cityArea: string;
  beirutAndAllLebanon: string;
  specialInstructionsNotes: string;
  specialInstructionsPlaceholder: string;
  directWhatsAppNotice: string;
  sendOrderViaWhatsApp: string;
  copyOrderDetails: string;
  copiedToClipboard: string;
  freePreviewGuarantee: string;
  cashOnDeliveryNotice: string;
  close: string;
  orderInquiryGenerated: string;
  forwardedToWhatsApp: string;
  openWhatsAppChat: string;
  continueShopping: string;
  highResolutionPrint: string;
  fastDeliveryDays: string;
  cashUsdOnlyNotice: string;
  canvasPricingNote: string;

  // Custom Print Studio View
  customStudioTitle: string;
  customStudioSubtitle: string;
  step1SelectProduct: string;
  step2UploadArtwork: string;
  step3DeliverySpecs: string;
  uploadPhotoOrArtwork: string;
  clickOrDragToUpload: string;
  supportsJpgPngWebp: string;
  removeImage: string;
  phoneCaseFinish: string;
  transparentFinish: string;
  matteFinish: string;
  deviceModelPlaceholder: string;
  deviceModelLabel: string;
  sendCustomDesignWhatsApp: string;

  // Offers Page
  seasonalOffersTitle: string;
  seasonalOffersSubtitle: string;
  discountBadge: string;
  allOffers: string;
  sortByPriceLowHigh: string;
  sortByPriceHighLow: string;
  noActiveOffers: string;
  customBundleTitle: string;
  customBundleDesc: string;
  requestBundleWhatsApp: string;

  // About & FAQ
  ourStoryTitle: string;
  ourStorySubtitle: string;
  frequentlyAskedQuestions: string;
  faqSubtitle: string;
  stillHaveQuestions: string;
  chatWithUsWhatsApp: string;

  // News Ticker
  flashNews: string;
  copyCode: string;
  codeCopied: string;

  // Footer
  footerAboutText: string;
  customProductsFooter: string;
  quickLinksFooter: string;
  directWhatsAppSupport: string;
  allRightsReserved: string;
  designedInLebanon: string;
  cashOnDeliveryAndWhish: string;
  payOnDelivery: string;
  customProducts: string;
}

export const translations: Record<Language, Translations> = {
  en: {
    home: 'Home',
    shop: 'Shop All',
    phoneCases: 'Phone Cases',
    canvasWallArt: 'Wall Art Canvas',
    photoFrames: 'Photo Frames',
    woodCrafts: 'Wood Crafts',
    keychains: 'Keychains',
    drinkingMugs: 'Photo Mugs',
    apparel: 'T-Shirts & Apparel',
    stickers: 'Stickers',
    hatsCaps: 'Hats & Caps',
    customPrint: 'Custom Print',
    blog: 'Blog & Articles',
    offers: 'Offers',
    aboutUs: 'About Us',
    faq: 'FAQ',
    adminPortal: 'Admin Portal',
    searchPlaceholder: 'Search products, styles, or tags...',
    searchProducts: 'Search products...',
    allCategories: 'All Categories',
    allTags: 'All Tags',
    resetFilters: 'Reset Filters',
    currency: 'USD ($)',
    usd: 'USD ($)',
    switchLanguage: 'العربية',
    arabicLebanese: 'العربية',
    english: 'English',

    announcementLocation: 'Custom Print on Demand • Lebanon',
    announcementDefault: 'Free delivery this Sunday on all custom orders across Lebanon!',

    heroEyebrow: '★ CUSTOM PRINT ON DEMAND STUDIO',
    heroHeadline: 'Print Your Image or Favorite Design on Anything.',
    heroTagline: "Lebanon's #1 custom studio for phone cases, canvas wall art, ceramic mugs, apparel, keychains, and photo frames. Direct WhatsApp ordering with image preview before printing.",
    heroWhatsAppBtn: 'Order on WhatsApp',
    heroCustomPrintBtn: 'Upload Your Custom Image',
    heroExploreCatalogBtn: 'Explore Catalog',
    heroPreviewNote: 'Free image mockup preview sent to you on WhatsApp before printing begins.',
    heroFastDelivery: 'Fast Delivery Across Lebanon',
    heroFastDeliverySub: 'Cash on Delivery ($ USD) • Whish Money • OMT',
    heroDirectWhatsApp: 'WhatsApp Direct Order',
    heroDirectWhatsAppSub: 'Chat with our design team instantly',
    heroQualityGuarantee: '100% Print Quality Guarantee',
    heroQualityGuaranteeSub: 'HD vibrant colors & lasting durability',

    customPrintOnDemand: 'CUSTOM PRINT ON DEMAND',
    personalizeByProduct: 'Personalize by Product',
    viewAllProducts: 'View All Products',
    allProducts: 'All Products',
    showingResults: 'Showing results for',
    noProductsFound: 'No products found matching your search.',
    clearFilters: 'Clear All Filters',
    orderViaWhatsApp: 'Order via WhatsApp',
    quickView: 'Quick View',
    viewDetails: 'View Details & Order',
    startingFrom: 'From',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    newDrop: 'NEW DROP',
    tapToZoom: 'Tap to zoom',
    productTags: 'Product Tags',
    activeTag: 'Active Tag',
    clearTagFilter: 'Clear tag filter',
    featuredProducts: 'Featured Products',
    sortByLabel: 'Sort:',
    sortOptionBlend: '✨ New & Classics Blend',
    sortOptionNewest: '🆕 Newest Releases',
    sortOptionOldest: '⭐ Original Classics',
    loadMoreProducts: 'Load More Products',
    showingItems: 'Showing',
    of: 'of',
    items: 'items',
    popularCustomProduct: 'POPULAR CUSTOM PRODUCT',
    customPhoneCases: 'Custom Phone Cases',
    phoneCasesDesc: 'Print your photos, pet portraits, couple moments, and custom designs onto custom phone cases for almost all phone brands in Lebanon. Image preview before printing request is available before printing.',
    viewAllCases: 'View All Cases',
    cherishedMemories: 'CHERISHED MEMORIES ON WALLS',
    customWallCanvas: 'Custom Wall Art Canvas',
    wallCanvasDesc: 'Turn your wedding pictures, family milestones, travel panoramas, and digital art into custom canvas wall art in Lebanon.',
    viewAllCanvas: 'View All Canvas',
    newProductDrop: '★ NEW PRODUCT DROP',
    customPhotoFrames: 'Custom Photo Frames',
    photoFramesDesc: 'Preserve your graduation, wedding, family, and art prints in gallery-grade wooden & modern frames with crystal glass.',
    viewAllFrames: 'View All Frames',

    customizeAndOrder: 'Customize & Order via WhatsApp',
    specsAndOptions: 'Select Specifications',
    chooseOption: 'Choose',
    yourName: 'Your Full Name',
    phoneNumber: 'Phone Number (WhatsApp)',
    cityArea: 'City / Delivery Region (Lebanon)',
    beirutAndAllLebanon: 'Beirut, Mount Lebanon, Tripoli, Saida, Bekaa, South...',
    specialInstructionsNotes: 'Custom Notes or Image Specs (Optional)',
    specialInstructionsPlaceholder: 'e.g., Please crop the photo to focus on the center, add "Happy Birthday Leo", or check transparent edges...',
    directWhatsAppNotice: 'When you tap below, this product and all your custom specifications will be sent directly to our Lebanese WhatsApp studio (+961 81773349). You can also send high-res photos directly in chat.',
    sendOrderViaWhatsApp: 'Send Order to WhatsApp',
    copyOrderDetails: 'Copy Order Text',
    copiedToClipboard: 'Order specs copied to clipboard!',
    freePreviewGuarantee: 'Free visual preview sent on WhatsApp before printing',
    cashOnDeliveryNotice: 'Cash on Delivery in US Dollars ($ USD) & Whish Money accepted',
    close: 'Close',
    orderInquiryGenerated: 'ORDER INQUIRY GENERATED',
    forwardedToWhatsApp: 'Forwarded to WhatsApp!',
    openWhatsAppChat: 'Open WhatsApp Chat Now',
    continueShopping: 'Continue Shopping',
    highResolutionPrint: 'High-Resolution HD Print',
    fastDeliveryDays: 'Fast delivery within 24 to 48 hours across Lebanon',
    cashUsdOnlyNotice: 'Cash on Delivery ($ USD) or Whish Money upon receipt',
    canvasPricingNote: 'Price varies for each canvas size. Confirm your chosen dimensions on WhatsApp for an exact price quote before dispatch.',

    customStudioTitle: 'Upload Your Custom Image or Design',
    customStudioSubtitle: 'Have a photo from your camera roll, a favourite memory, or custom artwork? Send it to us and our Lebanese print studio will craft it to perfection.',
    step1SelectProduct: 'Step 1: Choose Your Product',
    step2UploadArtwork: 'Step 2: Upload Your Photo or Art',
    step3DeliverySpecs: 'Step 3: Details & Forward to WhatsApp',
    uploadPhotoOrArtwork: 'Upload Your Photo or Design',
    clickOrDragToUpload: 'Click to choose image or drag and drop here',
    supportsJpgPngWebp: 'High-resolution PNG, JPG, or WebP recommended',
    removeImage: 'Remove Image',
    phoneCaseFinish: 'Phone Case Finish',
    transparentFinish: 'Clear Transparent Case',
    matteFinish: 'Black Matte Protective Case',
    deviceModelPlaceholder: 'e.g. iPhone 15 Pro Max, Samsung S24 Ultra, etc.',
    deviceModelLabel: 'Exact Phone Model / Size',
    sendCustomDesignWhatsApp: 'Send Custom Request to WhatsApp',

    seasonalOffersTitle: 'Special Deals & Bundles',
    seasonalOffersSubtitle: 'Exclusive limited-time savings on custom cases, canvas wall art, mugs, and personalized apparel in Lebanon.',
    discountBadge: 'SPECIAL OFFER',
    allOffers: 'All Offers',
    sortByPriceLowHigh: 'Price: Low to High',
    sortByPriceHighLow: 'Price: High to Low',
    noActiveOffers: 'No special offers at the moment. Check back soon or request a custom bundle on WhatsApp!',
    customBundleTitle: 'Looking for a Custom Bundle Deal?',
    customBundleDesc: 'Order 2 or more products (such as matching Phone Case + Photo Frame + Keychain) and receive an instant studio bundle discount. Direct courier delivery anywhere in Lebanon.',
    requestBundleWhatsApp: 'Request Bundle on WhatsApp',

    ourStoryTitle: 'About Coveristic',
    ourStorySubtitle: 'Handcrafted custom print studio based in Lebanon.',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqSubtitle: 'Everything you need to know about our Lebanese WhatsApp ordering process, payment in US Dollars, and delivery.',
    stillHaveQuestions: 'Still have questions? Chat with us directly!',
    chatWithUsWhatsApp: 'Chat with Us on WhatsApp',

    flashNews: 'FLASH NEWS',
    copyCode: 'Copy Code',
    codeCopied: 'Code copied!',

    footerAboutText: "Coveristic is Lebanon's dedicated custom print-on-demand studio. Personalized prints on custom Phone Cases for all brands, Wall Art Canvas, Photo Frames, Solid Wood Crafts, Keychains, Drinking Mugs, T-Shirts, and Die-Cut Stickers. Free WhatsApp visual preview before printing.",
    customProductsFooter: 'Custom Products',
    quickLinksFooter: 'Quick Links',
    directWhatsAppSupport: 'Direct WhatsApp Support',
    allRightsReserved: 'All rights reserved.',
    designedInLebanon: 'Crafted with care in Lebanon 🇱🇧',
    cashOnDeliveryAndWhish: 'Cash on Delivery ($ USD) • Whish Money • OMT',
    payOnDelivery: 'Cash on Delivery in USD ($) • Whish Money',
    customProducts: 'Custom Products'
  },

  ar: {
    home: 'الرئيسية',
    shop: 'كافة المنتجات',
    phoneCases: 'كفرات هواتف',
    canvasWallArt: 'لوحات الكانفاس',
    photoFrames: 'إطارات الصور',
    woodCrafts: 'مشغولات خشبية',
    keychains: 'ميداليات المفاتيح',
    drinkingMugs: 'اكواب',
    apparel: 'قمصان وهوديز',
    stickers: 'ستيكرز',
    hatsCaps: 'قبعات',
    customPrint: 'طباعة حسب الطلب',
    blog: 'المدونة والمقالات',
    offers: 'العروض والتخفيضات',
    aboutUs: 'من نحن',
    faq: 'الأسئلة الشائعة',
    adminPortal: 'لوحة التحكم',
    searchPlaceholder: 'ابحث عن منتج، تصميم أو وسم...',
    searchProducts: 'ابحث عن منتج...',
    allCategories: 'كافة الأقسام',
    allTags: 'كافة الوسوم',
    resetFilters: 'إعادة ضبط',
    currency: 'دولار ($)',
    usd: 'دولار ($)',
    switchLanguage: 'English',
    arabicLebanese: 'عربي',
    english: 'English',

    announcementLocation: 'طباعة مخصصة وتصاميم حسب الطلب • لبنان',
    announcementDefault: 'توصيل مجاني هذا الأحد لكافة الطلبيات المخصصة في جميع مناطق لبنان!',

    heroEyebrow: '★ استوديو الطباعة والتصاميم المخصصة في لبنان',
    heroHeadline: 'اطبع صورك وتصاميمك المفضلة على أي منتج تختاره!',
    heroTagline: 'الاستوديو الرائد في لبنان للطباعة على كفرات الهواتف المقاومة للصدمات، لوحات الكانفاس، الاكواب، القمصان، الميداليات وإطارات الصور. اطلب مباشرة عبر واتساب مع خدمة معاينة وبروفة مجانية قبل البدء بالطباعة.',
    heroWhatsAppBtn: 'اطلب مباشرة عبر واتساب',
    heroCustomPrintBtn: 'حمّل صورتك الخاصة واطبعها',
    heroExploreCatalogBtn: 'استكشف الكتالوج',
    heroPreviewNote: 'نرسل لكم بروفة ومعاينة رقمية للتصميم عبر واتساب لاعتمادها قبل بدء الطباعة.',
    heroFastDelivery: 'توصيل سريع لكافة مناطق لبنان',
    heroFastDeliverySub: 'الدفع نقداً عند الاستلام بالدولار ($ USD) • Whish Money',
    heroDirectWhatsApp: 'طلب سريع ومباشر عبر واتساب',
    heroDirectWhatsAppSub: 'فريق التصميم جاهز لمساعدتكم وتنسيق التصميم فوراً',
    heroQualityGuarantee: 'ضمان جودة وثبات الألوان 100%',
    heroQualityGuaranteeSub: 'أعلى دقة طباعة HD بخامات أصلية تدوم طويلاً',

    customPrintOnDemand: 'طباعة مخصصة حسب الطلب',
    personalizeByProduct: 'اختر حسب نوع المنتج',
    viewAllProducts: 'عرض كافة المنتجات',
    allProducts: 'كافة المنتجات',
    showingResults: 'نتائج البحث عن',
    noProductsFound: 'لم نتمكن من العثور على منتجات مطابقة لبحثكم، يرجى تجربة كلمات أخرى!',
    clearFilters: 'مسح كافة الفلاتر',
    orderViaWhatsApp: 'اطلب عبر واتساب',
    quickView: 'نظرة سريعة',
    viewDetails: 'التفاصيل والطلب',
    startingFrom: 'ابتداءً من',
    inStock: 'متوفر',
    outOfStock: 'غير متوفر حالياً',
    newDrop: 'وصل حديثاً',
    tapToZoom: 'اضغط للتكبير',
    productTags: 'وسوم المنتج',
    activeTag: 'الوسم المختار',
    clearTagFilter: 'إلغاء تصفية الوسم',
    featuredProducts: 'أبرز المنتجات',
    sortByLabel: 'ترتيب:',
    sortOptionBlend: '✨ تشكيلة متنوعة (جديد وكلاسيكي)',
    sortOptionNewest: '🆕 الأحدث أولاً',
    sortOptionOldest: '⭐ التشكيلة الأصلية الكلاسيكية',
    loadMoreProducts: 'عرض المزيد من المنتجات',
    showingItems: 'عرض',
    of: 'من أصل',
    items: 'منتج',
    popularCustomProduct: 'المنتج الأكثر طلباً وشهرة',
    customPhoneCases: 'كفرات هواتف حسب الطلب',
    phoneCasesDesc: 'اطبع صورك المفضلة وتصاميمك الخاصة على كفرات لكافة موديلات الهواتف في لبنان، مع بروفة ومعاينة مجانية قبل الطباعة.',
    viewAllCases: 'عرض جميع الكفرات',
    cherishedMemories: 'ذكرياتك الجميلة على جدران منزلك',
    customWallCanvas: 'لوحات كانفاس جدارية مخصصة',
    wallCanvasDesc: 'حوّل صور المناسبات الخاصة، وصور العائلة والمناظر الطبيعية إلى لوحات كانفاس جدارية بأعلى جودة ألوان في لبنان.',
    viewAllCanvas: 'عرض جميع اللوحات',
    newProductDrop: '★ منتج جديد وصل حديثاً',
    customPhotoFrames: 'إطارات صور فاخرة',
    photoFramesDesc: 'احفظ صور التخرج والمناسبات العزيزة بإطارات صور خشبية وعصرية مع زجاج كريستالي نقي.',
    viewAllFrames: 'عرض جميع الإطارات',

    customizeAndOrder: 'خصّص طلبك واطلبه مباشرة عبر واتساب',
    specsAndOptions: 'اختر المواصفات والخيارات',
    chooseOption: 'اختر',
    yourName: 'الاسم الكريم',
    phoneNumber: 'رقم الهاتف (واتساب)',
    cityArea: 'المنطقة / العنوان في لبنان',
    beirutAndAllLebanon: 'بيروت، جبل لبنان، طرابلس، صيدا، البقاع، الجنوب، عكار...',
    specialInstructionsNotes: 'ملاحظات خاصة حول الطلب أو الصورة (اختياري)',
    specialInstructionsPlaceholder: 'مثال: تركيز الصورة في الوسط، إضافة نص أو اسم، إزالة الخلفية...',
    directWhatsAppNotice: 'عند الضغط على الزر، سيتم إرسال طلبكم بكافة التفاصيل والمواصفات عبر واتساب إلى استوديو Coveristic (+961 81 773 349). كما يمكنكم إرسال الصورة بدقتها الكاملة مباشرة في المحادثة.',
    sendOrderViaWhatsApp: 'إرسال الطلب عبر واتساب 💬',
    copyOrderDetails: 'نسخ تفاصيل الطلب',
    copiedToClipboard: 'تم نسخ تفاصيل الطلب بنجاح!',
    freePreviewGuarantee: 'معاينة وبروفة مجانية عبر واتساب قبل بدء الطباعة',
    cashOnDeliveryNotice: 'الدفع نقداً عند الاستلام بالدولار الأمريكي ($ USD) أو عبر Whish Money',
    close: 'إغلاق',
    orderInquiryGenerated: 'تم إنشاء تفاصيل الطلب',
    forwardedToWhatsApp: 'تم تحويل الطلب إلى واتساب بنجاح! 💬',
    openWhatsAppChat: 'فتح محادثة واتساب الآن',
    continueShopping: 'متابعة التسوق',
    highResolutionPrint: 'طباعة عالية الدقة بألوان ثابتة HD',
    fastDeliveryDays: 'توصيل سريع خلال 24 إلى 48 ساعة لكافة مناطق لبنان',
    cashUsdOnlyNotice: 'الدفع نقداً عند الاستلام بالدولار الأمريكي ($ USD) أو عبر Whish Money',
    canvasPricingNote: 'يختلف السعر حسب مقاس اللوحة. سنؤكد معكم المقاس والسعر النهائي عبر واتساب قبل الطباعة والتوصيل.',

    customStudioTitle: 'حمّل صورتك أو تصميمك الخاص واطبعه',
    customStudioSubtitle: 'هل لديك صورة مفضلة، أو ذكرى جميلة، أو تصميم خاص؟ أرسلها إلينا وسيقوم استوديو Coveristic بطباعتها وفق أعلى معايير الجودة والدقة.',
    step1SelectProduct: 'الخطوة الأولى: اختر نوع المنتج',
    step2UploadArtwork: 'الخطوة الثانية: حمّل صورتك أو تصميمك',
    step3DeliverySpecs: 'الخطوة الثالثة: أدخل التفاصيل وأرسلها عبر واتساب',
    uploadPhotoOrArtwork: 'تحميل صورتك أو تصميمك',
    clickOrDragToUpload: 'اضغط لاختيار صورة من جهازك أو اسحبها إلى هنا',
    supportsJpgPngWebp: 'نوصي باختيار صور واضحة بصيغة PNG أو JPG أو WebP',
    removeImage: 'إزالة الصورة',
    phoneCaseFinish: 'نوع خامة الكفر',
    transparentFinish: 'كفر شفاف كريستال نقي',
    matteFinish: 'كفر أسود غير لامع (Matte) مقاوم للصدمات',
    deviceModelPlaceholder: 'مثال: iPhone 15 Pro Max أو Samsung S24 Ultra...',
    deviceModelLabel: 'موديل الهاتف بالتفصيل / المقاس',
    sendCustomDesignWhatsApp: 'إرسال التصميم والطلب عبر واتساب 💬',

    seasonalOffersTitle: 'عروض وتخفيضات مميزة',
    seasonalOffersSubtitle: 'وفّر أكثر مع عروضنا وباقاتنا الخاصة على كفرات الهواتف، ولوحات الكانفاس، والاكواب والهدايا في لبنان.',
    discountBadge: 'عرض خاص مميز 🔥',
    allOffers: 'جميع العروض',
    sortByPriceLowHigh: 'السعر: من الأقل للأعلى',
    sortByPriceHighLow: 'السعر: من الأعلى للأقل',
    noActiveOffers: 'لا توجد عروض مخصصة في الوقت الحالي، ولكن يسعدنا تواصلكم عبر واتساب لتقديم أفضل الأسعار للباقات!',
    customBundleTitle: 'هل تبحث عن باقة توفير خاصة؟',
    customBundleDesc: 'اطلب قطعتين أو أكثر (مثال: كفر هاتف + إطار صورة + ميدالية) للحصول على خصم فوري ومباشر مع توصيل لكافة مناطق لبنان والدفع نقداً عند الاستلام بالدولار.',
    requestBundleWhatsApp: 'طلب باقة مخصصة عبر واتساب',

    ourStoryTitle: 'من نحن - Coveristic',
    ourStorySubtitle: 'استوديو لبناني متخصص بالطباعة الفنية والتصاميم المخصصة حسب الطلب.',
    frequentlyAskedQuestions: 'الأسئلة الشائعة (FAQ)',
    faqSubtitle: 'كل ما تود معرفته عن خطوات الطلب السهلة عبر واتساب، الدفع نقداً عند الاستلام بالدولار، والتوصيل لكافة مناطق لبنان.',
    stillHaveQuestions: 'هل ما زال لديك أي سؤال؟ تواصل معنا مباشرة عبر واتساب!',
    chatWithUsWhatsApp: 'تواصل معنا عبر واتساب',

    flashNews: 'أخبار سريعة',
    copyCode: 'نسخ الرمز',
    codeCopied: 'تم نسخ الرمز!',

    footerAboutText: 'استوديو Coveristic هو وجهتكم الرائدة في لبنان للطباعة والتصاميم المخصصة حسب الطلب. نوفر طباعة فائقة الجودة لصوركم وتصاميمكم على كفرات الهواتف لكافة الموديلات، ولوحات الكانفاس الجدارية، وإطارات الصور، والقطع الخشبية، والميداليات، والاكواب، والقمصان وستيكرز. مع خدمة إرسال بروفة ومعاينة مجانية قبل الطباعة عبر واتساب.',
    customProductsFooter: 'منتجات حسب الطلب',
    quickLinksFooter: 'روابط سريعة',
    directWhatsAppSupport: 'خدمة العملاء عبر واتساب',
    allRightsReserved: 'جميع الحقوق محفوظة.',
    designedInLebanon: 'صُنع بحب في لبنان 🇱🇧',
    cashOnDeliveryAndWhish: 'الدفع عند الاستلام نقداً بالدولار ($ USD) • Whish Money • OMT',
    payOnDelivery: 'الدفع نقداً عند الاستلام بالدولار ($ USD) • Whish Money',
    customProducts: 'منتجات حسب الطلب'
  }
};

